/**
 * 路由 Suspense / 懒加载共用的品牌加载态（与 index.html 开机屏视觉一致，跟随全站主题）
 */
import React, { useEffect, useMemo, useState } from 'react';
import { APP_THEME_EVENT, getAppTheme, type AppThemeMode } from '@/utils/appTheme';

const TIPS = ['正在搬砖过海…', '静态资源在路上…', '马上就好…'] as const;

const THEME_STYLE: Record<
  AppThemeMode,
  { bg: string; fg: string; meta: string; track: string }
> = {
  dark: {
    bg: '#0c0c0c',
    fg: '#f4f1ea',
    meta: 'rgba(244, 241, 234, 0.45)',
    track: 'rgba(244, 241, 234, 0.12)',
  },
  light: {
    bg: '#f4f1ea',
    fg: '#1a1a1a',
    meta: 'rgba(26, 26, 26, 0.45)',
    track: 'rgba(26, 26, 26, 0.12)',
  },
  sepia: {
    bg: '#f0e6d2',
    fg: '#3d3426',
    meta: 'rgba(61, 52, 38, 0.5)',
    track: 'rgba(61, 52, 38, 0.15)',
  },
};

export const AppLoading: React.FC = () => {
  const [elapsed, setElapsed] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const [theme, setTheme] = useState<AppThemeMode>(() => getAppTheme());

  useEffect(() => {
    const sync = () => setTheme(getAppTheme());
    window.addEventListener(APP_THEME_EVENT, sync);
    return () => window.removeEventListener(APP_THEME_EVENT, sync);
  }, []);

  useEffect(() => {
    const t0 =
      typeof window !== 'undefined' && window.__K_BOOT_T0__
        ? window.__K_BOOT_T0__
        : Date.now();
    const tick = () => setElapsed(Math.floor((Date.now() - t0) / 1000));
    tick();
    const a = window.setInterval(tick, 1000);
    const b = window.setInterval(() => {
      setTipIndex((i) => (i + 1) % TIPS.length);
    }, 2800);
    return () => {
      window.clearInterval(a);
      window.clearInterval(b);
    };
  }, []);

  const palette = useMemo(() => THEME_STYLE[theme] ?? THEME_STYLE.dark, [theme]);

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-7"
      role="status"
      aria-live="polite"
      aria-label="页面加载中"
      style={{
        fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
        background: palette.bg,
        color: palette.fg,
      }}
    >
      <p className="m-0 text-[clamp(1.5rem,4vw,2rem)] font-medium lowercase tracking-[0.18em]">
        adoctors
      </p>
      <div
        className="h-0.5 w-[min(200px,42vw)] overflow-hidden rounded-sm"
        style={{ background: palette.track }}
        aria-hidden
      >
        <i className="block h-full w-2/5 bg-[#f8664e] motion-safe:animate-[boot-slide_1.4s_ease-in-out_infinite] not-italic" />
      </div>
      <p
        className="m-0 text-xs tracking-wider"
        style={{
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          color: palette.meta,
        }}
      >
        {TIPS[tipIndex]} · {elapsed}s
      </p>
      <style>{`
        @keyframes boot-slide {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(320%); }
        }
      `}</style>
    </div>
  );
};

export default AppLoading;
